import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import clsx from "clsx";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { MapPinIcon } from "@heroicons/react/24/solid";
import useCheckoutPage from "@/viewModels/checkout/useCheckoutPage";
import { formatPrice } from "@/utils/utils";
import { paymentMethodIdToNameMap } from "@/constants/mapping";
import CheckoutSteps from "@/components/features/checkout/CheckoutSteps";
import CartErrors from "@/components/features/cart/CartErrors";
import CartItems from "@/components/features/cart/CartItems";
import LabelInput from "@/components/ui/form/LabelInput";
import ErrorMessage from "@/components/ui/form/ErrorMessage";
import RadioCardSelector from "@/components/ui/form/RadioCardSelector";
import SpinnerLoading from "@/components/ui/loading/SpinnerLoading";
import CheckoutStepsSkeleton from "@/components/features/checkout/CheckoutStepsSkeleton";
import CartItemsSkeleton from "@/components/features/cart/CartItemsSkeleton";
import SEOMeta from "@/components/ui/meta/SEOMeta";
import { CheckoutStepsEnum, CvsTypeName, ShippingMethodType } from "@/constants/enums";
import type useCVSStore from "@/viewModels/checkout/features/useCVSStore";
import type useCheckoutForm from "@/viewModels/checkout/features/useCheckoutForm";

const Checkout = () => {
  const {
    cart,

    isShippingMethodsLoading,
    shippingOptions,

    control,
    errors,
    register,
    watch,
    handleFormSubmit,
    shippingMethodIdField,

    cvsStore,
    cvsMapFormWrapRef,
    queryCVSMap,

    isPageInitialized,
    shippingMethodType,
    cvsTypeName,
    allowedPaymentMethods,
    isOrderSubmitting,
    handleBeforeRedirect,
  } = useCheckoutPage();

  const { items = [], totals } = cart || {};

  if (!isPageInitialized) {
    return <CheckoutPageSkeleton />;
  }

  return (
    <main className="brand-main pb-24">
      {/* Step */}
      <CheckoutSteps currentStep={CheckoutStepsEnum.CHECKOUT} />

      <div className="brand-container max-w-screen-md flex-col">
        <section>
          {/* Cart Errors */}
          <CartErrors errors={cart?.errors || []} />
          {/* Cart Items */}
          <CartItems items={items} showProductLink={false} />

          {/* Items Total */}
          <div
            className={clsx(
              "brand-card",
              "mt-2 border-y [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 [&>*:not(hr)]:py-4",
            )}
          >
            <div className="brand-text-sm flex items-center justify-between">
              <span>商品總金額 ( 數量：{cart?.items_count} 件 )</span>
              <span className="text-black">{formatPrice(totals?.total_items ?? 0)}</span>
            </div>
            <hr />
            <div className="brand-text-sm flex items-center justify-between">
              <span>運費</span>
              <span className="brand-text-sm text-black">
                {isShippingMethodsLoading ? (
                  <SpinnerLoading colorClass="text-neutral" />
                ) : shippingMethodIdField ? (
                  formatPrice(totals?.total_shipping ?? "")
                ) : (
                  "請選擇運送方式"
                )}
              </span>
            </div>
          </div>
        </section>

        <form noValidate className="mt-8 flex flex-col gap-8">
          {/* Shipping Methods */}
          <fieldset>
            <legend>
              <SectionTitle>寄送方式</SectionTitle>
            </legend>
            <div className="alert mb-2 flex items-center gap-2 rounded-md text-left tracking-widest">
              <InformationCircleIcon className="size-5" />
              全店消費滿 $400 即享免運優惠 ( 期間限定優惠中! )
            </div>
            <Controller
              name="shippingMethodId"
              control={control}
              render={({ field }) => (
                <RadioCardSelector
                  isDescExpand
                  options={shippingOptions}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  errorMessage={errors?.shippingMethodId?.message}
                />
              )}
            />
          </fieldset>

          {/* CVS Map */}
          {shippingMethodType === ShippingMethodType.CVS && (
            <section>
              <SectionTitle>超商門市</SectionTitle>
              <div className="flex flex-col">
                <div className="order-2">
                  <button
                    type="button"
                    onClick={() => handleBeforeRedirect(queryCVSMap)}
                    className="brand-btn-accent-outline"
                  >
                    {cvsStore ? "更換門市" : "選擇門市"}
                  </button>
                  <ErrorMessage message={errors?.cvsStoreId?.message} />
                </div>
                <CVSStoreDetails cvsTypeName={cvsTypeName} cvsStore={cvsStore} control={control} />
              </div>
            </section>
          )}

          {/* Emil */}
          <fieldset>
            <legend>
              <SectionTitle>聯絡資訊</SectionTitle>
            </legend>
            <div className="brand-text-sm text-neutral">用於傳送訂單資訊和最給您</div>
            <LabelInput
              label="Email"
              type="email"
              registerProps={register("email")}
              value={watch("email")}
              error={errors?.email}
              classes="mt-4"
            />
          </fieldset>

          {/* Shipping Receiver */}
          <fieldset>
            <legend>
              <SectionTitle>收件人</SectionTitle>
            </legend>
            <div className="flex flex-col gap-4">
              <FromGroupWrapper>
                <LabelInput
                  label="姓氏"
                  type="text"
                  registerProps={register("shippingAddress.last_name")}
                  value={watch("shippingAddress.last_name")}
                  error={errors?.shippingAddress?.last_name}
                />
                <LabelInput
                  label="名字"
                  type="text"
                  registerProps={register("shippingAddress.first_name")}
                  value={watch("shippingAddress.first_name")}
                  error={errors?.shippingAddress?.first_name}
                />
              </FromGroupWrapper>
              <LabelInput
                label="手機號碼"
                type="text"
                registerProps={register("shippingAddress.phone")}
                value={watch("shippingAddress.phone")}
                error={errors?.shippingAddress?.phone}
              />
            </div>
          </fieldset>

          {/* Shipping Address */}
          <ShippingAddressFields
            shippingMethodType={shippingMethodType}
            register={register}
            watch={watch}
            errors={errors}
          />

          {/* Custom Note */}
          <fieldset>
            <legend>
              <SectionTitle>訂單備註 (選填)</SectionTitle>
            </legend>
            <textarea
              {...register("customNote")}
              rows={3}
              placeholder="留言給店家"
              className="brand-input-base textarea-neutral textarea textarea-bordered focus:textarea-info"
            ></textarea>
          </fieldset>

          {/* Payment Methods */}
          <fieldset>
            <legend>
              <SectionTitle>付款方式</SectionTitle>
            </legend>
            {(allowedPaymentMethods ?? []).length > 0 ? (
              <Controller
                name="paymentMethodId"
                control={control}
                render={({ field }) => (
                  <RadioCardSelector
                    options={allowedPaymentMethods.map((method) => ({
                      id: method.id,
                      title: paymentMethodIdToNameMap[method.id],
                      description: method.description,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors?.paymentMethodId?.message}
                  />
                )}
              />
            ) : (
              <div className="alert alert-warning flex items-center gap-2 rounded-md text-left">
                <ExclamationTriangleIcon className="size-5" />
                請先選擇運送方式
              </div>
            )}
          </fieldset>

          <section>
            <SectionTitle>訂單總結</SectionTitle>
            <div
              className={clsx(
                "brand-card",
                "border-y [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 [&>*:not(hr)]:py-4",
              )}
            >
              {/* Items Total */}
              <div className="brand-text-sm flex items-center justify-between">
                <span>商品總金額 ( 數量：{cart?.items_count} 件 )</span>
                <span className="text-black">{formatPrice(totals?.total_items ?? 0)}</span>
              </div>
              <hr />
              <div className="brand-text-sm flex items-center justify-between">
                <span>運費</span>
                <span className="brand-text-sm text-black">
                  {isShippingMethodsLoading ? (
                    <SpinnerLoading colorClass="text-neutral" />
                  ) : shippingMethodIdField ? (
                    formatPrice(totals?.total_shipping ?? "")
                  ) : (
                    "請選擇運送方式"
                  )}
                </span>
              </div>

              <hr className="-mx-4" />
              <div className="flex items-center justify-between">
                <span className="font-bold text-secondary">訂單付款金額</span>
                <span className="text-lg font-bold text-accent">
                  {formatPrice(totals?.total_price ?? 0)}
                </span>
              </div>
            </div>
            <p className="brand-text-xs mt-2 leading-relaxed text-neutral">
              提交訂單即表示您同意 Yuan Coffee 小院咖啡的
              <Link to="/terms-of-service" target="_blank" className="brand-link-with-underline">
                退換貨政策
              </Link>
              、
              <Link to="/return-policy" target="_blank" className="brand-link-with-underline">
                服務條款
              </Link>
              和
              <Link to="/privacy-policy" target="_blank" className="brand-link-with-underline">
                隱私政策
              </Link>
              ，我們會使用你的個人資料來處理你的訂單，以及用於隱私權政策中說明的其他用途。
            </p>

            {/* Fixed Submit Btn */}
            <div className="brand-fixed-panel z-fixedButton">
              <div className="brand-container w-full max-w-screen-md">
                <button
                  type="button"
                  onClick={handleFormSubmit}
                  disabled={isOrderSubmitting}
                  className="btn btn-primary m-auto mb-auto w-full"
                >
                  {isOrderSubmitting ? <SpinnerLoading /> : "下單付款"}
                </button>
              </div>
            </div>
          </section>
        </form>

        {/* CVS Form */}
        <div id="cvsMapFromWrap" ref={cvsMapFormWrapRef} />
      </div>

      {/* Meta Tag */}
      <SEOMeta pageTitle="結帳" />
    </main>
  );
};

const ShippingAddressFields = ({
  shippingMethodType,
  register,
  watch,
  errors,
}: {
  shippingMethodType: ShippingMethodType;
  register: ReturnType<typeof useCheckoutForm>["register"];
  watch: ReturnType<typeof useCheckoutForm>["watch"];
  errors: ReturnType<typeof useCheckoutForm>["errors"];
}) => {
  if (shippingMethodType === ShippingMethodType.CVS) return null;

  return (
    <>
      <fieldset>
        <legend>
          <SectionTitle>收件地址</SectionTitle>
        </legend>
        <div className="flex flex-col gap-4">
          <FromGroupWrapper>
            <LabelInput
              label="縣/市"
              type="text"
              registerProps={register("shippingAddress.state")}
              value={watch("shippingAddress.state")}
              error={errors?.shippingAddress?.state}
            />
            <LabelInput
              label="鄉/鎮/市/區"
              type="text"
              registerProps={register("shippingAddress.city")}
              value={watch("shippingAddress.city")}
              error={errors?.shippingAddress?.city}
            />
          </FromGroupWrapper>
          <LabelInput
            label="街道地址"
            type="text"
            registerProps={register("shippingAddress.address_1")}
            value={watch("shippingAddress.address_1")}
            error={errors?.shippingAddress?.address_1}
          />
          <FromGroupWrapper>
            <LabelInput
              label="郵遞區號"
              type="text"
              registerProps={register("shippingAddress.postcode")}
              value={watch("shippingAddress.postcode")}
              error={errors?.shippingAddress?.postcode}
            />
          </FromGroupWrapper>
          <LabelInput
            label="公司名稱 (選填)"
            type="text"
            registerProps={register("shippingAddress.company")}
            value={watch("shippingAddress.company")}
            error={errors?.shippingAddress?.company}
          />
        </div>
      </fieldset>
    </>
  );
};

const CVSStoreDetails = ({
  cvsStore,
  cvsTypeName,
  control,
}: {
  cvsStore: ReturnType<typeof useCVSStore>["cvsStore"];
  cvsTypeName: CvsTypeName;
  control: ReturnType<typeof useCheckoutForm>["control"];
}) => {
  if (!cvsStore) return null;

  return (
    <div className="brand-card mb-2 space-y-2">
      <div>
        <p className="flex items-center gap-1 text-base font-bold text-secondary">
          <MapPinIcon className="size-4" />
          {cvsTypeName}
          {`${cvsStore.CVSStoreName} `}
          <Controller
            name="cvsStoreId"
            control={control}
            render={({ field }) => <>({field.value})</>}
          />
        </p>
      </div>
      <p className="brand-text-sm text-neutral">{cvsStore.CVSAddress}</p>
    </div>
  );
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="brand-heading-sm mb-4 text-primary">{children}</h3>
);

const FromGroupWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-2">{children}</div>
);

const CheckoutPageSkeleton = () => (
  <div className="brand-container max-w-screen-md">
    <CheckoutStepsSkeleton />
    <CartItemsSkeleton />
  </div>
);

export default Checkout;
